import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { has, isObject } from "lodash";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import ChatMessages from "../../pages/Chat/ChatMessages";
import { MessagesSquare } from "lucide-react";
import { i18n } from "../../translate/i18n";
import { getChatTitle } from "./InternalChatListItem";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },
  welcome: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    textAlign: "center",
  },
}));

const InternalChatConversation = ({ chat, focusMessageId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [currentChat, setCurrentChat] = useState({});
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [justOpenedChat, setJustOpenedChat] = useState(false);
  const scrollToBottomRef = useRef();
  const isMounted = useRef(true);
  const loadingOlderForFocus = useRef(false);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [forwardChats, setForwardChats] = useState([]);
  const [selectedChatForForward, setSelectedChatForForward] = useState(null);

  const companyId = user?.companyId;
  const socket = companyId
    ? socketConnection({ companyId, userId: user?.id })
    : null;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const findMessages = async (chatId, isLoadMore = false, page = null) => {
    if (!isMounted.current || !chatId) return;

    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    let currentScrollHeight = 0;
    let currentScrollTop = 0;
    if (isLoadMore) {
      const el = document.querySelector(".messageList");
      currentScrollHeight = el?.scrollHeight || 0;
      currentScrollTop = el?.scrollTop || 0;
    }

    try {
      const pageToFetch = page !== null ? page : messagesPage;
      const { data } = await api.get(
        `/chats/${chatId}/messages?pageNumber=${pageToFetch}`
      );
      if (!isMounted.current) return;

      const isFirstPage = pageToFetch === 1;
      if (isLoadMore) setMessagesPage((prev) => prev + 1);
      setMessagesPageInfo(data);

      if (isFirstPage) {
        setMessages(data.records);
      } else {
        setMessages((prev) => [...data.records, ...prev]);
        if (isLoadMore) {
          setTimeout(() => {
            const el = document.querySelector(".messageList");
            if (el) {
              el.scrollTop =
                currentScrollTop + (el.scrollHeight - currentScrollHeight);
            }
          }, 100);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar mensagens do chat interno:", err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setLoadingMore(false);
        loadingOlderForFocus.current = false;
      }
    }
  };

  const selectChat = async (selected) => {
    if (!selected?.uuid && !selected?.id) {
      setCurrentChat({});
      setMessages([]);
      return;
    }

    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo({ hasMore: false });
    setJustOpenedChat(!focusMessageId);

    const markAsRead = async (chatData) => {
      if (!chatData?.id || !user?.id) return chatData;
      const current = (chatData.users || []).find((u) => u.userId === user.id);
      const unreads = Number(current?.unreads) || 0;
      if (unreads <= 0) return chatData;

      try {
        const { data } = await api.post(`/chats/${chatData.id}/read`, {
          userId: user.id,
        });
        return data || {
          ...chatData,
          users: (chatData.users || []).map((u) =>
            u.userId === user.id ? { ...u, unreads: 0 } : u
          ),
        };
      } catch (err) {
        console.error("Erro ao marcar chat interno como lido:", err);
        return {
          ...chatData,
          users: (chatData.users || []).map((u) =>
            u.userId === user.id ? { ...u, unreads: 0 } : u
          ),
        };
      }
    };

    try {
      const { data } = await api.get(`/chats/${selected.uuid || selected.id}`);
      if (!isMounted.current) return;
      const withRead = await markAsRead(data);
      if (!isMounted.current) return;
      setCurrentChat(withRead);
      await findMessages(withRead.id, false, 1);
    } catch (err) {
      if (!isMounted.current) return;
      const withRead = await markAsRead(selected);
      if (!isMounted.current) return;
      setCurrentChat(withRead);
      if (withRead.id) await findMessages(withRead.id, false, 1);
    }
  };

  useEffect(() => {
    selectChat(chat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.id, chat?.uuid]);

  // Se a mensagem alvo não está na página atual, carrega páginas anteriores
  useEffect(() => {
    if (!focusMessageId || !currentChat?.id || loading || loadingMore) return;
    if (loadingOlderForFocus.current) return;

    const exists = messages.some((m) => m.id === focusMessageId);
    if (exists) return;
    if (!messagesPageInfo?.hasMore) return;

    loadingOlderForFocus.current = true;
    findMessages(currentChat.id, true, messagesPage + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessageId, currentChat?.id, messages, messagesPageInfo, loading, loadingMore, messagesPage]);

  const normalizeIncomingMessage = (raw) => {
    if (!raw) return null;
    const msg = raw.dataValues ? { ...raw.dataValues, ...raw } : { ...raw };
    delete msg.dataValues;
    delete msg._previousDataValues;
    delete msg._changed;
    delete msg._options;
    delete msg.__replaceTempId;

    if (
      msg.mediaPath &&
      !String(msg.mediaPath).startsWith("http") &&
      !String(msg.mediaPath).startsWith("blob:") &&
      !String(msg.mediaPath).startsWith("/") &&
      msg.companyId
    ) {
      msg.mediaPath = `/public/company${msg.companyId}/chat/${msg.mediaPath}`;
    }
    return msg;
  };

  const addMessageToList = (raw, replaceTempId = null) => {
    const incoming = normalizeIncomingMessage(raw);
    if (!incoming) return;

    setMessages((prev) => {
      let next = prev.filter((msg) => {
        if (replaceTempId && String(msg.id) === String(replaceTempId)) {
          return false;
        }
        if (
          String(msg.id || "").startsWith("temp-") &&
          Number(msg.senderId) === Number(incoming.senderId) &&
          (msg.message === incoming.message ||
            msg.mediaName === incoming.mediaName)
        ) {
          return false;
        }
        return true;
      });

      if (next.some((msg) => msg.id === incoming.id)) {
        next = next.map((msg) =>
          msg.id === incoming.id ? { ...msg, ...incoming } : msg
        );
        return next;
      }
      return [...next, incoming];
    });
  };

  useEffect(() => {
    if (!socket || !companyId || !currentChat?.id) return undefined;

    const scrollDown = () => {
      setTimeout(() => {
        if (typeof scrollToBottomRef.current === "function") {
          scrollToBottomRef.current();
        }
      }, 80);
      setTimeout(() => {
        if (typeof scrollToBottomRef.current === "function") {
          scrollToBottomRef.current();
        }
      }, 250);
    };

    const onMessage = (data) => {
      if (!data) return;

      if (data.action === "new-message") {
        const incoming = data.newMessage || data.message;
        const chatId =
          incoming?.chatId || incoming?.chat?.id || data.chat?.id;
        if (!incoming || Number(chatId) !== Number(currentChat.id)) {
          return;
        }
        addMessageToList(incoming);
        scrollDown();
        return;
      }

      if (data.action === "create" || data.action === "update") {
        const incoming = data.message || data.newMessage;
        if (!incoming) return;
        const chatId =
          incoming?.chatId || incoming?.chat?.id || data.chat?.id;
        if (Number(chatId) !== Number(currentChat.id)) return;

        if (data.action === "create") {
          addMessageToList(incoming);
          scrollDown();
        } else {
          const normalized = normalizeIncomingMessage(incoming);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === normalized.id ? { ...m, ...normalized } : m
            )
          );
        }
        return;
      }

      if (data.action === "edit-message") {
        const incoming = normalizeIncomingMessage(data.message);
        if (!incoming) return;
        setMessages((prev) =>
          prev.map((m) => (m.id === incoming.id ? { ...m, ...incoming } : m))
        );
        return;
      }

      if (data.action === "delete" || data.action === "delete-message") {
        const incoming = normalizeIncomingMessage(data.message) || {
          id: data.id,
        };
        if (!incoming?.id) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === incoming.id
              ? {
                  ...m,
                  ...incoming,
                  isDeleted: true,
                  message:
                    incoming.message ||
                    m.message ||
                    "Esta mensagem foi apagada",
                  mediaPath: null,
                  mediaUrl: null,
                  mediaName: null,
                  forwardedFrom: null,
                  replyTo: null,
                }
              : m
          )
        );
      }
    };

    socket.on(`company-${companyId}-chat-${currentChat.id}`, onMessage);
    return () => {
      socket.off(`company-${companyId}-chat-${currentChat.id}`, onMessage);
    };
  }, [socket, companyId, currentChat?.id]);

  const sendMessage = async (contentMessage) => {
    if (!currentChat?.id) return;
    setLoading(true);
    try {
      await api.post(`/chats/${currentChat.id}/messages`, {
        message: contentMessage,
      });
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    }
    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (!messagesPageInfo?.hasMore || loadingMore || !currentChat?.id) return;
    await findMessages(currentChat.id, true, messagesPage + 1);
  };

  const addOptimisticMessage = (msg) => {
    if (!msg) return;
    const replaceTempId = msg.__replaceTempId || null;
    addMessageToList(msg, replaceTempId);
  };

  const handleEditMessage = (message) => {
    if (!message) return;
    if (new Date() - new Date(message.createdAt) > 10 * 60 * 1000) {
      toast.warn("Só é possível editar mensagens enviadas há menos de 10 minutos.");
      return;
    }
    setSelectedMessage(message);
    setEditText(message.message || "");
    setEditModalOpen(true);
  };

  const handleDeleteMessage = (message) => {
    if (!message) return;
    setSelectedMessage(message);
    setDeleteModalOpen(true);
  };

  const handleForwardMessage = async (message) => {
    if (!message) return;
    setSelectedMessage(message);
    setSelectedChatForForward(null);
    setForwardModalOpen(true);
    try {
      const { data } = await api.get("/chats");
      setForwardChats(Array.isArray(data?.records) ? data.records : []);
    } catch (err) {
      setForwardChats([]);
      toastError(err);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedMessage?.id) return;
    try {
      await api.put(`/chats/messages/${selectedMessage.id}`, {
        message: editText,
      });
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === selectedMessage.id
            ? { ...msg, message: editText, isEdited: true }
            : msg
        )
      );
      setEditModalOpen(false);
      setSelectedMessage(null);
      setEditText("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedMessage?.id) return;
    try {
      await api.delete(`/chats/messages/${selectedMessage.id}`);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === selectedMessage.id
            ? {
                ...msg,
                isDeleted: true,
                message: "Esta mensagem foi apagada",
                mediaPath: null,
                mediaUrl: null,
                mediaName: null,
                forwardedFrom: null,
                replyTo: null,
              }
            : msg
        )
      );
      setDeleteModalOpen(false);
      setSelectedMessage(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleForwardSubmit = async () => {
    if (!selectedMessage?.id || !selectedChatForForward) return;
    try {
      await api.post(`/chats/messages/${selectedMessage.id}/forward`, {
        targetChatId: selectedChatForForward,
      });
      toast.success("Mensagem encaminhada");
      setForwardModalOpen(false);
      setSelectedMessage(null);
      setSelectedChatForForward(null);
    } catch (err) {
      toastError(err);
    }
  };

  if (!chat || !isObject(currentChat) || !has(currentChat, "id")) {
    return (
      <Paper elevation={0} className={classes.welcome}>
        <MessagesSquare size={36} />
        <Typography variant="h6">Chat Interno</Typography>
        <Typography variant="body2" color="textSecondary">
          Selecione uma conversa à esquerda para começar.
        </Typography>
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      <ChatMessages
        chat={currentChat}
        scrollToBottomRef={scrollToBottomRef}
        pageInfo={messagesPageInfo}
        messages={messages}
        loading={loading}
        loadingMore={loadingMore}
        handleSendMessage={sendMessage}
        handleLoadMore={loadMoreMessages}
        justOpenedChat={justOpenedChat}
        setJustOpenedChat={setJustOpenedChat}
        addOptimisticMessage={addOptimisticMessage}
        focusMessageId={focusMessageId}
        onEdit={handleEditMessage}
        onDelete={handleDeleteMessage}
        onForward={handleForwardMessage}
      />

      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Mensagem</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Mensagem"
            type="text"
            fullWidth
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            multiline
            minRows={3}
            maxRows={10}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)} color="secondary">
            {i18n.t("chatIndex.cancel") || "Cancelar"}
          </Button>
          <Button onClick={handleEditSubmit} color="primary">
            {i18n.t("chatIndex.save") || "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Excluir mensagem</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja apagar esta mensagem?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} color="secondary">
            {i18n.t("chatIndex.cancel") || "Cancelar"}
          </Button>
          <Button onClick={handleDeleteSubmit} color="primary">
            {i18n.t("chatIndex.delete") || "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forwardModalOpen}
        onClose={() => setForwardModalOpen(false)}
      >
        <DialogTitle>Encaminhar Mensagem</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Selecione o chat</InputLabel>
            <Select
              value={selectedChatForForward || ""}
              onChange={(e) => setSelectedChatForForward(e.target.value)}
            >
              {forwardChats
                .filter((c) => Number(c.id) !== Number(currentChat.id))
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {getChatTitle(c, user)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardModalOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleForwardSubmit}
            color="primary"
            disabled={!selectedChatForForward}
          >
            Encaminhar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InternalChatConversation;
